import {
  drugService,
  type DrugDetailRequest,
  type DrugInfoResponse,
  type DrugSearchRequest,
} from '@/entities/drug/api/drug.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const drugKeys = {
  all: ['drugs'] as const,
  lists: () => [...drugKeys.all, 'list'] as const,
  list: (search: DrugSearchRequest, pageable: Pageable) =>
    [...drugKeys.lists(), search, pageable] as const,
  details: () => [...drugKeys.all, 'detail'] as const,
  detail: (request: DrugDetailRequest) =>
    [...drugKeys.details(), request] as const,
  latestReleasedRefYm: () =>
    [...drugKeys.all, 'latest-released-ref-ym'] as const,
};

export interface DrugListParams {
  search: DrugSearchRequest;
  pageable: Pageable;
  /** 기준년월 기본값(최신 공개 월)을 받기 전엔 조회하지 않을 때 false */
  enabled?: boolean;
}

/**
 * 의약품 목록 조회.
 * 레거시 `drugInfo.js` — 기준년월·검색 필터 + 서버 페이지네이션.
 */
export const useDrugList = ({
  search,
  pageable,
  enabled = true,
}: DrugListParams) =>
  usePaginatedQuery<DrugSearchRequest, DrugInfoResponse>({
    queryKeyPrefix: drugKeys.lists(),
    search,
    pageable,
    enabled,
    queryFn: async ({ search: s, pageable: p }) => drugService.getDrugs(s, p),
  });

/**
 * 공개된 최신 기준 연월.
 * 의약품 목록 기준년월 기본값 — 현재 월은 아직 공개 전일 수 있어 404가 난다.
 * 공개된 월이 없으면 404 — 재시도해도 같으므로 바로 실패시킨다.
 */
export const useLatestReleasedDrugRefYm = () =>
  useQuery({
    queryKey: drugKeys.latestReleasedRefYm(),
    queryFn: () => drugService.getLatestReleasedRefYm(),
    retry: false,
  });

/**
 * 의약품 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportDrugs = () =>
  useMutation({
    mutationFn: (search: DrugSearchRequest) => drugService.exportDrugs(search),
  });

/**
 * 의약품 상세 조회.
 * 레거시 `druginfoDetails.js` GET `/admin/drugs/detail` 대응 (조회 전용).
 */
export const useDrugDetail = (request: DrugDetailRequest | null) =>
  useQuery({
    queryKey: request ? drugKeys.detail(request) : [],
    queryFn: async () => {
      if (!request) throw new Error('refYm and drugCode are required');
      return drugService.getDrugDetail(request);
    },
    enabled: request != null && !!request.refYm && !!request.drugCode,
  });

/** 처방 검수 약품 추가 모달 — 의약품 코드 조회 */
export const useDrugSearchByCode = () =>
  useMutation({
    mutationFn: ({ drugCode, refYm }: { drugCode: string; refYm: string }) =>
      drugService.searchDrugByCode(drugCode, refYm),
  });

/** 처방 검수 — 의약품명 검색 */
export const useDrugSearchByName = () =>
  useMutation({
    mutationFn: ({ drugName, refYm }: { drugName: string; refYm: string }) =>
      drugService.searchDrugsByName(drugName, refYm),
  });

/** 의약품 상세 수정 — PATCH /admin/drugs/{refYm}/{drugCode} */
export const useUpdateDrug = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      refYm,
      drugCode,
      data,
    }: {
      refYm: string;
      drugCode: string;
      data: Record<string, unknown>;
    }) => {
      await drugService.updateDrug(refYm, drugCode, data);
      return { refYm, drugCode };
    },
    onSuccess: ({ refYm, drugCode }) => {
      queryClient.invalidateQueries({
        queryKey: drugKeys.detail({ refYm, drugCode }),
      });
      queryClient.invalidateQueries({ queryKey: drugKeys.lists() });
    },
  });
};
